import Axios from 'axios'
import moment from 'moment'
import Cookie from 'js-cookie'

async function authenticateUser({ commit, state, dispatch }, loginData) {
  try {
    commit('setFormState', {
      form: 'login',
      state: 'submitting',
    })

    // We need to use the FormData class because the reem backend expects
    // the parameters form www-urlencoded and this is the only way I found to
    // make that request with axios.
    const data = new FormData()
    data.append('user', loginData.user)
    data.append('pwd', loginData.pwd)
    data.append('authorized_yes', 1)

    const authUrl = new URL(state.urls.authentication)
    authUrl.searchParams.append('scope', '1')
    authUrl.searchParams.append('response_type', 'token')
    authUrl.searchParams.append('redirect_uri', state.global.appUrl)
    authUrl.searchParams.append('client_id', state.crm.clientId)
    authUrl.searchParams.append('getaccesstokenjson', '1')

    const result = await Axios.post(authUrl.href, data)
    commit('userAuthenticationRequestFinished', result)
    commit('setFormState', {
      form: 'login',
      state: false,
    })

    if (result && result.data && result.data.access_token) {
      commit('setAuthToken', result.data.access_token)
      dispatch('initializeApp')
    }
  } catch (err) {
    if (
      err.response &&
      err.response.status === 401 &&
      err.response.data.error_description
    ) {
      commit('userAuthenticationFailed', err.response.data.error_description)
    } else {
      dispatch('logError', 'The authenticateUser request failed: ' + err)
    }
  }
  commit('setFormState', {
    form: 'login',
    state: false,
  })
}

function doAutologin({ commit, dispatch }, accessToken) {
  if (!accessToken) {
    dispatch('logError', 'Failed autologin. AccessToken missing/empty.')
  }
  commit('setAuthCookie', accessToken)
  commit('setAuthToken', accessToken)
}

async function logoutUser({ commit, dispatch, state }) {
  // This is necessary so our backend known when access tokens are invalid.
  try {
    commit('setFormState', {
      form: 'logout',
      state: 'submitting',
    })
    let getParameters = {
      access_token: state.authentication.backendAccessToken,
    }
    await Axios.post(
      `${state.urls.reem}/users/logout`,
      {},
      {
        params: getParameters,
      }
    )
  } catch (err) {
    dispatch('logError', 'The logout request failed: ' + err)
  }
  commit('userLoggedOut')
  commit('setFormState', {
    form: 'logout',
    state: false,
  })
}

async function loadContractData({ commit, state, dispatch }) {
  try {
    let getParameters = {
      access_token: state.authentication.backendAccessToken,
      fields:
        'get_vertrag,get_kunde,get_ansprechpartner,get_lieferadresse,get_rechnungsadressekonto,get_preisartikel,get_preise,get_stdvkpreis,get_adressen',
    }
    const url = `${state.urls.reem}/cube/zaehlpunkte/${state.cube.name}`
    const result = await Axios.get(url, {
      params: getParameters,
    })

    if (!result || !result.data) {
      throw new Error('Contract-data request returned unexpected format.')
    }

    commit('contractDataReceived', result)
    dispatch('loadInvoices')
  } catch (err) {
    dispatch('logError', 'Requesting contract data failed: ' + err)
  }
}

async function selectInvoiceYear({ commit }, year) {
  commit('selectInvoiceYear', year)
}

async function saveDefaultSellingPrice({ commit, state, dispatch }, newPrice) {
  try {
    commit('setFormState', {
      form: 'defaultSellingPrice',
      state: 'submitting',
    })

    // We need to use the FormData class because the reem backend expects
    // the parameters form www-urlencoded and this is the only way I found to
    // make that request with axios.
    const data = new FormData()
    data.append('preis', parseFloat(newPrice))
    const url = `${state.urls.reem}/artikelbasispreis/${state.cube.name}`

    let getParameters = {
      access_token: state.authentication.backendAccessToken,
    }

    const result = await Axios.post(url, data, {
      params: getParameters,
    })

    commit('setFormState', {
      form: 'defaultSellingPrice',
      state: false,
    })

    if (!result || !result.data) {
      throw new Error('Update base selling price request failed.')
    }

    let priceData = {
      currentPrice: result.data.basispreis_aktuell.preis,
      nextPrice: false,
      nextPriceDate: false,
    }

    if (result.data.basispreis_next) {
      priceData.nextPrice = result.data.basispreis_next.preis
      priceData.dateNextPrice = moment(
        result.data.basispreis_next.gueltig_ab,
        'X'
      ).toDate()
    } else {
      throw new Error(
        'Error storing next base price locally. The api result did not contain new price information, or changed the data format.'
      )
    }

    commit('setDefaultSellingPrices', priceData)
  } catch (err) {
    dispatch('logError', 'Error updating default selling price: ' + err)
  }
}

/**
 * Send and email with reset instructions to the user. Dispatched when the user
 * submits form on the password reset page (while being logged out).
 *
 * @param state
 * @param commit
 * @param dispatch
 * @param email
 * @returns {Promise<void>}
 */
async function startPasswordReset({ state, commit, dispatch }, email) {
  try {
    commit('setFormState', {
      form: 'passwordReset',
      state: 'submitting',
    })
    // We need to use the FormData class because the reem backend expects
    // the parameters form www-urlencoded and this is the only way I found to
    // make that request with axios.
    const data = new FormData()
    data.append('user', email)

    const url = `${state.urls.authentication}/v001/forgotpasswordreset?client_id=${state.crm.clientId}`
    const result = await Axios.post(url, data)

    if (!result || !result.data) {
      throw new Error('Start pw reset request failed.')
    }

    const message = state.strings.authentication.passwordResetMailSent
    commit('setFormState', {
      form: 'passwordReset',
      state: 'finished',
    })
    commit('passwordResetProcessStarted', message)
  } catch (err) {
    dispatch('logError', `Error starting pw reset: ${err}`)
    commit('setFormState', {
      form: 'passwordReset',
      state: false,
    })
  }
}

/**
 * Send an email to the user with password reset instructions. Dispatched from
 * the settings page via link while being logged in.
 *
 * TODO Refactor the password reset to get rid of code duplication.
 *
 * @param state
 * @param commit
 * @param dispatch
 * @returns {Promise<void>}
 */
async function startChangePassword({ state, commit, dispatch }) {
  try {
    commit('setFormState', {
      form: 'changePassword',
      state: 'submitting',
    })
    // We need to use the FormData class because the reem backend expects
    // the parameters form www-urlencoded and this is the only way I found to
    // make that request with axios.
    const data = new FormData()
    data.append('user', state.settings.contractData.email)
    const url = `${state.urls.authentication}/v001/forgotpasswordreset?client_id=${state.crm.clientId}`
    const result = await Axios.post(url, data)

    if (!result || !result.data) {
      throw new Error('Start change password request failed.')
    }

    const message = state.strings.authentication.passwordResetMailSent
    commit('setFormState', {
      form: 'changePassword',
      state: 'finished',
    })
    commit('passwordResetProcessStarted', message)
  } catch (err) {
    dispatch('logError', `Error starting change password: ${err}`)
    commit('setFormState', {
      form: 'changePassword',
      state: false,
    })
  }
}

/**
 * The user has typed a new passsword and wants to store it. Let the backend do
 * whatever needs to be done with this.
 *
 * @param state
 * @param dispatch
 * @param data
 * @returns {Promise<void>}
 */
async function changeUserPassword({ state, dispatch }, data) {
  try {
    // We need to use the FormData class because the reem backend expects
    // the parameters form www-urlencoded and this is the only way I found to
    // make that request with axios.
    const formData = new FormData()
    formData.append('pwd', data.password)
    const url = `${state.urls.authentication}/v001/pwdreset`

    const getParameters = {
      scope: 1,
      response_type: 'token',
      client_id: state.crm.clientId,
      redirect_uri: state.global.appUrl,
      getaccesstokenjson: 1,
      key: data.token,
    }

    const result = await Axios.post(url, formData, {
      params: getParameters,
    })

    if (!result || !result.data) {
      throw new Error('Passwort reset request failed.')
    }

    Cookie.set(state.authentication.cookieName, result.data.access_token, {
      expires: state.authentication.cookieLifeTime,
      secure: true,
    })

    // Force a reload.
    window.location.href = '/'
  } catch (err) {
    dispatch('logError', `Error resetting user password: ${err}`)
  }
}

async function saveSpecialSellingPriceForConsumer(
  { state, commit, dispatch },
  data
) {
  try {
    // We need to use the FormData class because the reem backend expects
    // the parameters form www-urlencoded and this is the only way I found to
    // make that request with axios.
    const formData = new FormData()
    formData.append('preis', data.price)
    formData.append('cubeid', data.consumer)
    const url = `${state.urls.reem}/artikelsonderpreis/${state.cube.name}`

    let getParameters = {
      access_token: state.authentication.backendAccessToken,
    }

    const result = await Axios.post(url, formData, {
      params: getParameters,
    })

    if (!result || !result.data) {
      throw new Error('Set consumer price request failed.')
    }

    let currentPrice
    if (result.data.sonderpreis_aktuell) {
      currentPrice = parseFloat(result.data.sonderpreis_aktuell.preis)
    } else {
      currentPrice = state.settings.producerSettings.defaultSellingPrice
    }

    commit('updateConsumerPrice', {
      currentPrice: currentPrice,
      cubeName: result.data.sonderpreis_next.cubeidentifiers[0],
      nextPrice: parseFloat(result.data.sonderpreis_next.preis),
      dateNextPrice: moment(
        result.data.sonderpreis_next.gueltig_ab,
        'X'
      ).toDate(),
    })
    return result
  } catch (err) {
    dispatch('logError', `Error setting consumer price: ${err}`)
  }
}

async function getConsumerPrices({ state, commit, dispatch }) {
  try {
    const url = `${state.urls.reem}/artikelsonderpreise/${state.cube.name}`

    let getParameters = {
      access_token: state.authentication.backendAccessToken,
      fields: 'get_konto,get_artikel,get_vertrag,get_kunde',
    }

    const result = await Axios.get(url, {
      params: getParameters,
    })

    if (!result || !result.data) {
      throw new Error('Get consumer prices request failed.')
    }

    commit('consumersPricesReceived', result)
  } catch (err) {
    dispatch('logError', `Error getting consumer prices: ${err}`)
  }
}

async function loadProducerPrices({ state, commit, dispatch }, cubes = false) {
  try {
    if (!cubes) {
      cubes = state.map.globalCubesList.filter((cube) => {
        return cube.hasProduction
      })
    }
    const cubeNames = cubes.map((cube) => {
      return cube.params.cubeName
    })

    if (!cubeNames) {
      return
    }
    const url = `${state.urls.reem}/artikelproduzentenpreise/${cubeNames.join(
      ','
    )}`

    let getParameters = {
      access_token: state.authentication.backendAccessToken,
      fields: 'get_konto,get_artikel',
    }

    const result = await Axios.get(url, {
      params: getParameters,
    })

    if (!result || !result.data || !result.data.list) {
      throw new Error(`Get producer prices for map request failed.`)
    }

    commit('producerPricesReceived', result)
  } catch (err) {
    dispatch('logError', `Error getting producer prices for map: ${err}`)
  }
}

async function removeSpecialPriceForConsumer(
  { state, commit, dispatch },
  cubeName
) {
  try {
    let getParameters = {
      access_token: state.authentication.backendAccessToken,
    }
    const url = `${state.urls.reem}/artikelsonderpreis/beenden/${state.cube.name}`
    const postData = new FormData()
    postData.append('cubeid', cubeName)

    const result = await Axios.post(url, postData, {
      params: getParameters,
    })

    if (!result || !result.data) {
      throw new Error(`Error removing special price for cube: ${cubeName}.`)
    }

    commit('removeSpecialPriceForConsumer', result.data)
  } catch (err) {
    dispatch('logError', 'Removing special price failed: ' + err)
  }
}

/**
 * Note that we need to load the invoices after receiving the contract data
 * because in the result of the invoices request there might be multiple
 * contracts and we need to use the correct one.
 *
 * @param state
 * @param commit
 * @param dispatch
 * @returns {Promise<void>}
 */
async function loadInvoices({ state, commit, dispatch }) {
  try {
    let getParameters = {
      access_token: state.authentication.backendAccessToken,
      fields: 'get_rechnungen_simple,get_dokumenteliste,get_dokument_httplink',
    }
    const result = await Axios.get(`${state.urls.reem}/efvertragliste`, {
      params: getParameters,
    })

    if (!result || !result.data) {
      throw new Error('Invoices request returned unexpected format.')
    }

    commit('invoicesReceived', result.data)
  } catch (err) {
    dispatch('logError', 'Requesting invoices failed: ' + err)
  }
}

async function getDefaultSellingPrice({ state, commit, dispatch }) {
  try {
    let getParameters = {
      access_token: state.authentication.backendAccessToken,
    }
    const url = `${state.urls.reem}/artikelbasispreis/${state.cube.name}`
    const result = await Axios.get(url, {
      params: getParameters,
    })

    if (!result || !result.data) {
      throw new Error('Default selling price!')
    }

    let data = {
      currentPrice: result.data.basispreis_aktuell
        ? result.data.basispreis_aktuell.preis
        : false,
      nextPrice: false,
      dateNextPrice: false,
    }

    if (result.data.basispreis_next) {
      data.nextPrice = result.data.basispreis_next.preis
      data.dateNextPrice = moment(
        result.data.basispreis_next.gueltig_ab,
        'X'
      ).toDate()
    }
    commit('setDefaultSellingPrices', data)
  } catch (err) {
    dispatch('logError', 'Requesting invoices failed: ' + err)
  }
}

async function updateCubeData({ state, dispatch }, data) {
  try {
    const url = `${state.urls.reem}/cube/uploadbild/${state.cube.name}`
    const postData = new FormData()
    postData.append('cubeid', state.cube.name)

    if (typeof data.crop !== 'undefined') {
      postData.append('lkwidth', 200)
      postData.append('lkheight', 250)
      postData.append('lkscale', data.crop.scale)
      postData.append('lkrotation', data.crop.rotation)
      postData.append('lkx', data.crop.x)
      postData.append('lky', data.crop.y)
    }

    if (typeof data.image !== 'undefined') {
      postData.append('cubebild', data.image)
    }
    if (typeof data.description !== 'undefined') {
      postData.append('cubebeschreibung', data.description)
    }

    const result = await Axios.post(url, postData, {
      params: {
        access_token: state.authentication.backendAccessToken,
      },
      headers: {
        'content-type': 'multipart/form-data',
      },
    })

    if (!result || !result.data) {
      throw new Error(`cubeId: ${state.cube.name}.`)
    }
  } catch (err) {
    dispatch('logError', 'Error updating cube data: ' + err)
  }
}

export default {
  loadContractData,
  selectInvoiceYear,
  saveDefaultSellingPrice,
  startPasswordReset,
  changeUserPassword,
  saveSpecialSellingPriceForConsumer,
  getConsumerPrices,
  loadProducerPrices,
  authenticateUser,
  logoutUser,
  removeSpecialPriceForConsumer,
  loadInvoices,
  getDefaultSellingPrice,
  startChangePassword,
  doAutologin,
  updateCubeData,
}
